import { ChevronDown } from 'lucide-react';
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Input,
  VStack,
} from '@eluve/components';
import { MEDICAL_SPECIALTIES, isKnownSpecialty } from '@eluve/utils';

export interface SpecialtySelectorComboBoxProps {
  selectedSpecialty: string | null;
  onSelectedSpecialty: (specialty: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const SpecialtySelectorComboBox: FC<SpecialtySelectorComboBoxProps> = ({
  selectedSpecialty,
  onSelectedSpecialty,
  disabled = false,
  placeholder = 'Select a specialty',
}) => {
  const [otherSpecialty, setOtherSpecialty] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isOtherSpecialty =
    selectedSpecialty != null && !isKnownSpecialty(selectedSpecialty);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSpecialtyChange = useCallback(
    (specialty: string | null) => {
      if (specialty === 'Other') {
        setIsEditing(true);
        setOtherSpecialty('');
        return;
      }
      setIsEditing(false);
      onSelectedSpecialty(specialty);
    },
    [onSelectedSpecialty],
  );

  const handleOtherSpecialtyBlur = useCallback(() => {
    const specialty = (otherSpecialty ?? '').trim();
    if (specialty && specialty !== selectedSpecialty) {
      onSelectedSpecialty(specialty);
      setIsEditing(false);
    } else if (specialty) {
      // User has entered something but it matches existing - just exit edit mode
      setIsEditing(false);
    }
    // If empty, keep isEditing true so input stays visible
  }, [otherSpecialty, selectedSpecialty, onSelectedSpecialty]);

  const handleOtherSpecialtyChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setOtherSpecialty(event.target.value);
    },
    [],
  );

  const handleOtherSpecialtyKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (['Enter', 'Escape'].includes(event.key)) {
        inputRef.current?.blur();
      }
    },
    [],
  );

  const displayValue =
    isEditing || isOtherSpecialty ? 'Other' : selectedSpecialty ?? placeholder;

  return (
    <VStack gap={2} align="stretch">
      <Combobox disabled={disabled}>
        <ComboboxSelectButton
          className="w-full justify-between"
          dropdownIcon={
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          }
        >
          {displayValue}
        </ComboboxSelectButton>
        <ComboboxDropdown
          searchPlaceholder="Search specialties..."
          className="max-h-60 min-w-52"
        >
          {selectedSpecialty !== null && (
            <ComboboxOption onSelect={() => handleSpecialtyChange(null)}>
              <Icon className="mr-2" name="X" size="xs" />
              Clear specialty
            </ComboboxOption>
          )}
          {[...MEDICAL_SPECIALTIES, 'Other'].map((specialty) => (
            <ComboboxOption
              key={specialty}
              onSelect={() => handleSpecialtyChange(specialty)}
            >
              <ComboboxSelectCheck
                className="mr-2"
                selected={
                  isEditing || isOtherSpecialty
                    ? specialty === 'Other'
                    : specialty === selectedSpecialty
                }
              />
              {specialty}
            </ComboboxOption>
          ))}
        </ComboboxDropdown>
      </Combobox>
      {(isEditing || isOtherSpecialty) && (
        <Input
          disabled={disabled}
          onBlur={handleOtherSpecialtyBlur}
          onChange={handleOtherSpecialtyChange}
          onFocus={() => setIsEditing(true)}
          onKeyDown={handleOtherSpecialtyKeyDown}
          placeholder="Enter your specialty"
          ref={inputRef}
          value={
            isEditing
              ? otherSpecialty ?? selectedSpecialty ?? ''
              : selectedSpecialty ?? ''
          }
        />
      )}
    </VStack>
  );
};
